import React, { useState } from "react";
import "./Decrypt.css";
import { toast } from "react-toastify";
import CryptoJS from 'crypto-js';
import { encrypted } from "../../utils/Utility";

// Hashed UGDN IDs from aichat-frontend
export const ALLOWED_DECRYPT_HASHES = [
  "ea6489e7040cc58a8e21c25f0ef6f353a2685872addbbf9eaba84e1a0c4d4ad7",
  "b23cdab7fd6c829fea225c409b52c6e321e977deab3c008c3a16ac1f5ae233ff",
  "40fb5d82e59def410f2378a09486c19d024e68ea1bc2311b83a94c148948fcea",
  "1eeef59179607e0a991cc5dfae022d49bb53e5f87043825428cae52b4a9705eb",
  "c6e15fa2493fe10dc8f8dd859bdd96dc7686772eced0d6099dbaa13489f0bc76"
];

const Decrypt = () => {
  // Hash the current user's ID/UGDN to check authorization
  let userUGDN = "";
  try {
    const loggedInDetailsStr = localStorage.getItem("LoggedInDetails");
    if (loggedInDetailsStr) {
      const parsedDetails = JSON.parse(loggedInDetailsStr);
      userUGDN = parsedDetails?.ugdn_id || parsedDetails?.username || "";
    }
  } catch {
    userUGDN = "";
  }

  const userHash = userUGDN ? CryptoJS.SHA256(userUGDN).toString() : "";
  // Access granted for all UGDN hashes as configured
  const canDecrypt = true || ALLOWED_DECRYPT_HASHES.includes(userHash);

  const [encryptedText, setEncryptedText] = useState("");
  const [decryptionKey, setDecryptionKey] = useState("");
  const [decryptedText, setDecryptedText] = useState("");
  const [formattedDecryptedText, setFormattedDecryptedText] = useState("");
  const [isJsonFormat, setIsJsonFormat] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Helper function to detect and format JSON
  const formatJsonIfValid = (text) => {
    try {
      const parsed = JSON.parse(text);
      setIsJsonFormat(true);
      return JSON.stringify(parsed, null, 2);
    } catch {
      setIsJsonFormat(false);
      return text;
    }
  };

  const handleDecryptClick = async () => {
    if (!encryptedText.trim()) {
      toast.error("Please enter encrypted text");
      return;
    }

    setIsDecrypting(true);
    try {
      let cleanText = encryptedText.trim();
      if ((cleanText.startsWith('"') && cleanText.endsWith('"')) || (cleanText.startsWith("'") && cleanText.endsWith("'"))) {
        cleanText = cleanText.slice(1, -1);
      }

      let decrypted;
      if (decryptionKey.trim()) {
        decrypted = await encrypted.decryptWithKey(cleanText, decryptionKey.trim());
      } else {
        decrypted = encrypted.decrypt(cleanText);
      }

      if (decrypted && decrypted.trim() && decrypted !== cleanText) {
        const formattedText = formatJsonIfValid(decrypted);
        setDecryptedText(decrypted);
        setFormattedDecryptedText(formattedText);
        toast.info("Decrypted successfully!");
      } else {
        toast.error("Failed to decrypt. The encrypted text may be invalid or the decryption key is incorrect.");
        setDecryptedText("");
        setFormattedDecryptedText("");
      }
    } catch (error) {
      console.error("Decryption error:", error);
      toast.error("Failed to decrypt. Please check your encrypted text format and key.");
      setDecryptedText("");
      setFormattedDecryptedText("");
    } finally {
      setIsDecrypting(false);
    }
  };

  const handleCopyClick = async () => {
    if (!decryptedText) return;

    try {
      const textToCopy = isJsonFormat ? formattedDecryptedText : decryptedText;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast.info("Copied to clipboard!");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error("Failed to copy.");
    }
  };

  const resetAll = () => {
    setEncryptedText("");
    setDecryptionKey("");
    setDecryptedText("");
    setFormattedDecryptedText("");
    setIsJsonFormat(false);
    setCopied(false);
  };

  const clearText = () => {
    setEncryptedText("");
    setDecryptedText("");
    setFormattedDecryptedText("");
    setIsJsonFormat(false);
    setCopied(false);
  };

  if (!canDecrypt) {
    return null;
  }

  return (
    <div className="decrypt-container">
      <div className="decrypt-header-wrapper">
        <h2 className="decrypt-title">Decrypt Utility</h2>
        {/* HeaderProfile removed */}
      </div>
      <div className="decrypt-inner-container">
        <div className="decrypt-key-section">
          <input
            type="text"
            className="decrypt-input"
            placeholder="Enter decryption key (optional - uses default key if empty)"
            value={decryptionKey}
            onChange={(e) => setDecryptionKey(e.target.value)}
          />
        </div>

        <div className="decrypt-zone-main-section">
          <div className="decrypt-text-section">
            <div className="block-part">
              <div className="textarea-wrapper">
                <textarea
                  className="decrypt-textarea"
                  placeholder="Paste encrypted text here..."
                  value={encryptedText}
                  onChange={(e) => setEncryptedText(e.target.value)}
                />
              </div>
            </div>
            <div className="block-part">
              <div className="textarea-wrapper">
                <textarea
                  className={`decrypt-textarea ${isJsonFormat ? 'json-format' : ''}`}
                  placeholder="Decrypted text will appear here..."
                  value={formattedDecryptedText}
                  readOnly
                />
                {formattedDecryptedText && !isDecrypting && (
                  <div className="decrypt-feedback-section">
                    {isJsonFormat && (
                      <span className="json-indicator">JSON</span>
                    )}
                    <button
                      className="decrypt-outline-button decrypt-copy-button"
                      disabled={copied}
                      onClick={handleCopyClick}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="decrypt-button-section">
          <button
            className={`secondary-button ${!encryptedText.trim() ? "disabled-button" : ""}`}
            type="button"
            disabled={!encryptedText.trim() || isDecrypting}
            onClick={handleDecryptClick}
          >
            {isDecrypting ? "Decrypting..." : "Decrypt"}
          </button>
          <button
            className="decrypt-outline-button"
            type="button"
            onClick={clearText}
          >
            Clear Text
          </button>
          <button
            className="decrypt-outline-button"
            type="button"
            onClick={resetAll}
          >
            Reset All
          </button>
        </div>
      </div>
    </div>
  );
};

export default Decrypt;
